import React, { useState, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../../../config';
import styles from './DespesasPopup.module.css';

// ==========================================================================
//   FUNÇÕES UTILITÁRIAS PARA NÚMEROS
// ==========================================================================
const parseBRLNumber = (value) => {
    if (typeof value !== 'string' || !value) return 0;
    return parseFloat(value.replace(/\./g, '').replace(',', '.'));
};

const formatBRLNumber = (value) => {
    const number = parseFloat(value);
    if (isNaN(number)) return '';
    return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
    }).format(number);
};


const EditDespesaPopup = ({ despesa, fornecedores, onClose, onSave, tiposDespesa = [], onAddNewTipoDespesa }) => {
    const [formData, setFormData] = useState({
        data: new Date(despesa.data).toISOString().slice(0, 10),
        tipo_despesa: despesa.tipo_despesa || '',
        descricao: despesa.descricao || '',
        odometro: formatBRLNumber(despesa.odometro) || '',
        fornecedor_id: despesa.fornecedor_id || '',
        numero_nota_fiscal: despesa.numero_nota_fiscal || '',
        quantidade_itens: formatBRLNumber(despesa.quantidade_itens) || '1,00',
        valor_unitario: formatBRLNumber(despesa.valor_unitario) || '',
    });

    const [showOdometro, setShowOdometro] = useState(!!despesa.odometro);

    const valorTotalCalculado = useMemo(() => {
        const qtd = parseBRLNumber(formData.quantidade_itens);
        const vlrUnit = parseBRLNumber(formData.valor_unitario);
        return qtd * vlrUnit;
    }, [formData.quantidade_itens, formData.valor_unitario]);

    const handleNumericInputChange = (e) => {
        const { name, value } = e.target;
        let formattedValue = value.replace(/[^0-9,]/g, '');
        setFormData(prev => ({ ...prev, [name]: formattedValue }));
    };

    const handleInputChange = async (e) => {
        const { name, value } = e.target;
        if (name === 'tipo_despesa' && value === '--CRIAR-NOVO--') {
            if (onAddNewTipoDespesa) {
                const novoTipo = await onAddNewTipoDespesa();
                if (novoTipo) {
                    setFormData(prev => ({ ...prev, tipo_despesa: novoTipo.nome }));
                } else {
                    setFormData(prev => ({ ...prev, tipo_despesa: '' }));
                }
            } else {
                console.error("Handler 'onAddNewTipoDespesa' não foi fornecido.");
                Swal.fire('Erro!', 'Ocorreu um erro interno ao tentar adicionar o tipo.', 'error');
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleOdometroCheckboxChange = (e) => {
        const isChecked = e.target.checked;
        setShowOdometro(isChecked);
        if (!isChecked) {
            setFormData(prev => ({ ...prev, odometro: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const despesaParaEnviar = {
            ...formData,
            valor_unitario: parseBRLNumber(formData.valor_unitario),
            quantidade_itens: parseBRLNumber(formData.quantidade_itens),
            odometro: formData.odometro ? parseBRLNumber(formData.odometro) : null
        };

        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API_BASE_URL}/api/veiculos/despesas/${despesa.id}`, despesaParaEnviar, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            Swal.fire({
                title: 'Sucesso!',
                text: 'Despesa atualizada com sucesso.',
                icon: 'success',
                confirmButtonText: 'OK',
                customClass: {
                    container: 'swal-container-custom',
                    popup: 'swal-popup-custom',
                    title: 'swal-title-custom',
                    confirmButton: 'swal-confirm-button-custom',
                }
            });
            onSave();
        } catch (error) {
            console.error("Erro ao atualizar despesa:", error);
            Swal.fire('Erro!', 'Não foi possível atualizar a despesa.', 'error');
        }
    };

    const formatCurrency = (value) => value ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value) : 'R$ 0,00';

    return (
        <div className={styles.popupOverlay}>
            <div className={styles.popupContent}>
                <header className={styles.popupHeader}><h2>Editar Despesa</h2><button onClick={onClose} className={styles.closePopupBtn}>&times;</button></header>
                <form onSubmit={handleSubmit} className={styles.popupBody}>
                    <div className={styles.formAddDespesa}>
                        <div className={styles.formGroup}><label htmlFor="data">Data *</label><input type="date" id="data" name="data" value={formData.data} onChange={handleInputChange} className={styles.formInput} required /></div>
                        <div className={styles.formGroup}>
                            <label htmlFor="tipo_despesa">Tipo *</label>
                            <select id="tipo_despesa" name="tipo_despesa" value={formData.tipo_despesa} onChange={handleInputChange} className={styles.formSelect} required>
                                <option value="">Selecione...</option>
                                {tiposDespesa.map(tipo => (
                                    <option key={tipo.id} value={tipo.nome}>{tipo.nome}</option>
                                ))}
                                <option value="--CRIAR-NOVO--" className={styles.optionAddNew}>
                                    » Adicionar Novo Tipo...
                                </option>
                            </select>
                        </div>
                        <div className={styles.formGroup} style={{ gridColumn: 'span 2' }}><label htmlFor="descricao">Descrição *</label><input type="text" id="descricao" name="descricao" value={formData.descricao} onChange={handleInputChange} className={styles.formInput} required /></div>
                        <div className={styles.formGroup} style={{ gridColumn: 'span 2' }}><label htmlFor="fornecedor_id">Fornecedor</label><select id="fornecedor_id" name="fornecedor_id" value={formData.fornecedor_id} onChange={handleInputChange} className={styles.formSelect}><option value="">Selecione...</option>{fornecedores.map(f => <option key={f.id} value={f.id}>{f.nome_empresa}</option>)}</select></div>
                        <div className={styles.formGroup}><label htmlFor="numero_nota_fiscal">Nº Nota Fiscal</label><input type="text" id="numero_nota_fiscal" name="numero_nota_fiscal" value={formData.numero_nota_fiscal} onChange={handleInputChange} className={styles.formInput} /></div>
                        <div className={styles.formGroup}><label htmlFor="quantidade_itens">Quantidade *</label><input type="text" id="quantidade_itens" name="quantidade_itens" value={formData.quantidade_itens} onChange={handleNumericInputChange} className={styles.formInput} required /></div>
                        <div className={styles.formGroup}><label htmlFor="valor_unitario">Valor Unitário (R$) *</label><input type="text" id="valor_unitario" name="valor_unitario" value={formData.valor_unitario} onChange={handleNumericInputChange} className={styles.formInput} required /></div>
                        <div className={styles.formGroup}><label>Valor Total</label><input type="text" value={formatCurrency(valorTotalCalculado)} className={styles.formInput} disabled style={{backgroundColor: '#e9ecef', fontWeight: 'bold'}} /></div>
                        
                        <div className={styles.formGroupCheck}>
                            <input
                                type="checkbox"
                                id="showOdometroEdit"
                                className={styles.formCheckInput}
                                checked={showOdometro}
                                onChange={handleOdometroCheckboxChange}
                            />
                            <label htmlFor="showOdometroEdit">Informar Horímetro/KM?</label>
                        </div>
                        
                        {showOdometro && (
                            <div className={styles.formGroup} style={{ gridColumn: 'span 2' }}>
                                <label htmlFor="odometro">Horímetro/KM</label>
                                <input 
                                    type="text" 
                                    id="odometro" 
                                    name="odometro" 
                                    value={formData.odometro} 
                                    onChange={handleNumericInputChange} 
                                    className={styles.formInput} 
                                />
                            </div>
                        )}
                    </div>
                    <footer className={styles.popupActions}><button type="button" className={styles.btnClose} onClick={onClose}>Cancelar</button><button type="submit" className={styles.addButton}>Salvar Alterações</button></footer>
                </form>
            </div>
        </div>
    );
};

export default EditDespesaPopup;